/**
 * JAISIU-4450 — classic Office-style **coloured** attachment type icons.
 *
 * ## Why
 *
 * Until now every attachment glyph was a monochrome Lucide outline
 * stroked with `currentColor`. That made a PDF, an Excel workbook and a
 * .docx three different *shapes* but the same *colour*, and — worse —
 * the chip CSS (`svg { fill: none; stroke: currentColor; }`) actively
 * flattened any colour that a future coloured glyph tried to carry.
 *
 * Operators expect the classic Office treatment: PDF red, spreadsheet
 * green, document blue. Everything else (image, video, audio, code,
 * generic file) keeps the muted monochrome glyph so the colour stays
 * meaningful — colour only ever signals a *document family*.
 *
 * ## How
 *
 * The coloured glyphs are real SVGs with their own `fill` / `stroke`
 * attributes (a filled page body in the type colour plus a white
 * badge), **not** a `currentColor` stroke. That means the wrapper CSS
 * must not force `fill: none` on them — see
 * `.att-icon--pdf` / `--spreadsheet` / `--document` in
 * `ui/src/styles/chat/layout.css`, which scope the flattening rule to
 * the monochrome case only.
 *
 * Every attachment render site goes through
 * {@link attachmentIconForRenderKind}, so a PDF is red in the
 * transcript chip, the outbound chip, the composer tray and the
 * overlay header alike.
 */
import { html, type TemplateResult } from "lit";
import { icons, type IconName } from "../icons.ts";
import {
  attachmentIconNameForKind,
  type AttachmentKind,
  type SessionFileKind,
} from "./attachment-types.ts";

/**
 * The colour families that get a coloured glyph. Everything outside
 * this set keeps the monochrome outline glyph.
 */
export type AttachmentIconColorFamily = "pdf" | "spreadsheet" | "document";

/** Class applied to the icon wrapper for each coloured family. */
export type AttachmentIconColorClass =
  | "att-icon--pdf"
  | "att-icon--spreadsheet"
  | "att-icon--document";

/**
 * The literal Office-ish hex values. Kept as constants so the CSS and
 * the SVG fills cannot drift apart — the CSS rules read them from the
 * same palette via custom properties, and the node tests assert these
 * exact values.
 */
export const ATTACHMENT_ICON_COLORS = {
  pdf: "#d93025",
  spreadsheet: "#1e8e3e",
  document: "#1a73e8",
} as const satisfies Record<AttachmentIconColorFamily, string>;

/**
 * Map an attachment kind (render family *or* gateway family) onto its
 * colour family, or `null` when the kind has no coloured treatment.
 *
 * The gateway family is accepted for the same reason
 * {@link attachmentIconNameForKind} accepts it: a composer chip in
 * flight carries the coarse gateway `kind` (`document` for a PDF,
 * `tabular` for an Excel file).
 *
 * Note the asymmetry with the icon-name mapper: the gateway's
 * `document` kind covers *both* PDFs and .docx files, so a bare
 * `document` kind is **not** enough to decide "red" vs "blue". The
 * colour decision therefore needs the MIME/filename, which is exactly
 * what {@link attachmentIconColorClassForAttachment} has.
 */
export function attachmentIconColorFamilyForKind(
  kind: AttachmentKind | SessionFileKind | string | undefined,
): AttachmentIconColorFamily | null {
  switch (kind) {
    case "pdf":
      return "pdf";
    case "spreadsheet":
    case "tabular":
      return "spreadsheet";
    case "document":
      return "document";
    default:
      return null;
  }
}

/**
 * Whether a MIME type identifies a PDF.
 */
function isPdfMime(mime: string | undefined): boolean {
  if (!mime) return false;
  const m = mime.trim().toLowerCase();
  return m === "application/pdf" || m === "application/x-pdf" || m.endsWith("/pdf");
}

/**
 * Whether a MIME type identifies a spreadsheet (Excel / ODS / CSV).
 */
function isSpreadsheetMime(mime: string | undefined): boolean {
  if (!mime) return false;
  const m = mime.trim().toLowerCase();
  return (
    m === "text/csv" ||
    m === "application/vnd.ms-excel" ||
    m === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    m === "application/vnd.openxmlformats-officedocument.spreadsheetml.template" ||
    m === "application/vnd.oasis.opendocument.spreadsheet" ||
    m.endsWith("spreadsheetml.sheet")
  );
}

/**
 * Whether a MIME type identifies a word-processor document.
 */
function isDocumentMime(mime: string | undefined): boolean {
  if (!mime) return false;
  const m = mime.trim().toLowerCase();
  return (
    m === "application/msword" ||
    m === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    m === "application/vnd.openxmlformats-officedocument.wordprocessingml.template" ||
    m === "application/vnd.oasis.opendocument.text" ||
    m === "application/rtf" ||
    m.endsWith("wordprocessingml.document")
  );
}

function extensionOf(filename: string | undefined): string {
  if (!filename) return "";
  const base = filename.split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot < 0 || dot === base.length - 1) return "";
  return base.slice(dot + 1).toLowerCase();
}

/**
 * Resolve the colour family for a full attachment descriptor.
 *
 * Resolution order (most specific first):
 *
 *  1. **MIME** — the only reliable signal. `application/pdf` is red
 *     even when the gateway called the file `document`.
 *  2. **Filename extension** — `report.pdf`, `book.xlsx`, `notes.docx`.
 *  3. **Kind** — the last resort. A bare `document` kind is blue
 *     (it is overwhelmingly a .docx in practice), `tabular` is green.
 *
 * `undefined` means "no coloured treatment" — the caller renders the
 * monochrome glyph.
 */
export function attachmentIconColorFamilyForAttachment(a: {
  kind?: AttachmentKind | SessionFileKind | string;
  mimeType?: string;
  mime?: string;
  filename?: string;
}): AttachmentIconColorFamily | null {
  const mime = a.mimeType ?? a.mime;
  if (isPdfMime(mime)) return "pdf";
  if (isSpreadsheetMime(mime)) return "spreadsheet";
  if (isDocumentMime(mime)) return "document";

  const ext = extensionOf(a.filename);
  if (ext === "pdf") return "pdf";
  if (ext === "xls" || ext === "xlsx" || ext === "xlsm" || ext === "csv" || ext === "ods") {
    return "spreadsheet";
  }
  if (
    ext === "doc" ||
    ext === "docx" ||
    ext === "docm" ||
    ext === "odt" ||
    ext === "rtf" ||
    ext === "txt" ||
    ext === "md"
  ) {
    return "document";
  }

  return attachmentIconColorFamilyForKind(a.kind);
}

/**
 * The CSS class that applies the coloured treatment, or `""` when the
 * kind keeps its monochrome glyph.
 */
export function attachmentIconColorClass(
  family: AttachmentIconColorFamily | null | undefined,
): AttachmentIconColorClass | "" {
  switch (family) {
    case "pdf":
      return "att-icon--pdf";
    case "spreadsheet":
      return "att-icon--spreadsheet";
    case "document":
      return "att-icon--document";
    default:
      return "";
  }
}

/**
 * Convenience wrapper: attachment descriptor → CSS class (or `""`).
 */
export function attachmentIconColorClassForAttachment(a: {
  kind?: AttachmentKind | SessionFileKind | string;
  mimeType?: string;
  mime?: string;
  filename?: string;
}): AttachmentIconColorClass | "" {
  return attachmentIconColorClass(attachmentIconColorFamilyForAttachment(a));
}

/* ------------------------------------------------------------------ */
/* Coloured glyphs                                                     */
/* ------------------------------------------------------------------ */

/**
 * The coloured page shape shared by all three families: a filled page
 * with a folded corner. `currentColor` is deliberately **not** used —
 * the fill is the family colour so the glyph survives a CSS rule that
 * sets `stroke: currentColor`.
 */
function coloredPage(inner: TemplateResult, color: string): TemplateResult {
  return html`
    <svg viewBox="0 0 24 24" class="att-icon__svg att-icon__svg--colored">
      <path
        d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
        fill=${color}
      />
      <polyline points="14 2 14 8 20 8" fill=${color} />
      <path d="M14 2v6h6" fill="#ffffff" opacity="0.35" />
      ${inner}
    </svg>
  `;
}

/** Red PDF glyph — the classic red document with a white "PDF" band. */
const pdfGlyph: TemplateResult = coloredPage(
  html`
    <rect x="5" y="12.5" width="14" height="6.5" rx="1" fill="#ffffff" />
    <text
      x="12"
      y="17.4"
      text-anchor="middle"
      font-family="system-ui, -apple-system, Segoe UI, sans-serif"
      font-size="4.6"
      font-weight="700"
      fill=${ATTACHMENT_ICON_COLORS.pdf}
    >
      PDF
    </text>
  `,
  ATTACHMENT_ICON_COLORS.pdf,
);

/** Green spreadsheet glyph — the classic green sheet with white cells. */
const spreadsheetGlyph: TemplateResult = coloredPage(
  html`
    <rect x="5" y="11" width="14" height="8" rx="1" fill="#ffffff" />
    <line x1="5" x2="19" y1="13.7" y2="13.7" stroke=${ATTACHMENT_ICON_COLORS.spreadsheet} stroke-width="0.9" />
    <line x1="5" x2="19" y1="16.4" y2="16.4" stroke=${ATTACHMENT_ICON_COLORS.spreadsheet} stroke-width="0.9" />
    <line x1="9.7" x2="9.7" y1="11" y2="19" stroke=${ATTACHMENT_ICON_COLORS.spreadsheet} stroke-width="0.9" />
    <line x1="14.3" x2="14.3" y1="11" y2="19" stroke=${ATTACHMENT_ICON_COLORS.spreadsheet} stroke-width="0.9" />
  `,
  ATTACHMENT_ICON_COLORS.spreadsheet,
);

/** Blue document glyph — the classic blue page with white text lines. */
const documentGlyph: TemplateResult = coloredPage(
  html`
    <rect x="7" y="12" width="10" height="1.8" rx="0.9" fill="#ffffff" />
    <rect x="7" y="15" width="10" height="1.8" rx="0.9" fill="#ffffff" />
    <rect x="7" y="18" width="6.5" height="1.8" rx="0.9" fill="#ffffff" />
  `,
  ATTACHMENT_ICON_COLORS.document,
);

/**
 * The coloured glyph for a colour family, or `null` when the family is
 * unknown (the caller then falls back to the monochrome glyph).
 */
export function attachmentIconSvgForFamily(
  family: AttachmentIconColorFamily | null | undefined,
): TemplateResult | null {
  switch (family) {
    case "pdf":
      return pdfGlyph;
    case "spreadsheet":
      return spreadsheetGlyph;
    case "document":
      return documentGlyph;
    default:
      return null;
  }
}

/**
 * The glyph to render for an attachment kind / descriptor.
 *
 * Returns the coloured SVG for PDF / spreadsheet / document and the
 * shared monochrome Lucide glyph for every other kind (image, video,
 * audio, code, generic file).
 */
export function attachmentIconForRenderKind(
  kind: AttachmentKind | SessionFileKind | string | undefined,
  opts: { mimeType?: string; mime?: string; filename?: string } = {},
): TemplateResult {
  const family = attachmentIconColorFamilyForAttachment({
    kind,
    mimeType: opts.mimeType,
    mime: opts.mime,
    filename: opts.filename,
  });
  const colored = attachmentIconSvgForFamily(family);
  if (colored) return colored;
  return icons[attachmentIconNameForKind(kind) as IconName];
}

/**
 * The icon *name* (as opposed to the SVG) for an attachment kind —
 * kept so the existing name-level assertions (JAISIU-4435) stay
 * meaningful. Coloured families keep their type-specific names
 * (`filePdf`, `fileSpreadsheet`, `fileText`).
 */
export function attachmentIconNameForRenderKind(
  kind: AttachmentKind | SessionFileKind | string | undefined,
): IconName {
  return attachmentIconNameForKind(kind);
}
